//import tree from '@/data/categories.json'
import { Category, Content, MainCategory, SubCategory } from '@/lib/categories/categories'
import { FilterData } from '@/lib/filters/filters'
import { parseKeywords } from '@/lib/utils'
import { AssetType, SearchPath } from '@/lib/assets/types'
import { BUILD_DATE } from '@/lib/config'

export type CategoriesData = {
  mainCategories: MainCategory[]
  categories: Category[]
  subCategories: SubCategory[]
  contents: Content[]
  filters: FilterData[]
}

let dataCache: any = undefined

const loadData: () => Promise<any> = async () => {
  if (dataCache) {
    return dataCache
  }

  console.debug('Loading categories data from remote source...')

  const response = await fetch('https://public.productioncrate.com/metadata/categories.json?build=' + BUILD_DATE)

  if (!response.ok) {
    throw new Error(`Failed to load categories: ${response.status} ${response.statusText}`)
  }

  const data = await response.json()
  console.debug('Categories data loaded successfully, caching it for future use.')
  dataCache = data

  return data
}

export const getData = async (): Promise<CategoriesData> => {
  const tree = await loadData()

  return {
    mainCategories: tree.mainCategories.map((mainCategory: any) => ({
      id: mainCategory.id,
      name: mainCategory.name,
      slug: mainCategory.slug,
      image: mainCategory.image,
      searchPath: mainCategory.search_path as SearchPath,
      banner: mainCategory.banner,
      title: mainCategory.title || mainCategory.name,
      description: mainCategory.description,
      keywords: parseKeywords(mainCategory.keywords),

      // Include categories in mainCategory
      categories: mainCategory.categories?.map((category: any) => ({
        id: category.id,
        name: category.name,
        slug: category.slug,
        image: category.image,
        icon: category.icon,
        title: category.title || category.name,
        description: category.description,
        keywords: parseKeywords(category.keywords),
        assetType: category.asset_type as AssetType,
        mainCategoryId: mainCategory.id,
        mainCategorySlug: mainCategory.slug,
      })),
    })),
    categories: tree.mainCategories.flatMap((mainCategory: any) =>
      mainCategory.categories?.map((category: any) => ({
        id: category.id,
        name: category.name,
        slug: category.slug,
        image: mainCategory.image,
        icon: category.icon,
        title: category.title || category.name,
        keywords: parseKeywords(category.keywords),
        description: category.description,
        assetType: category.asset_type as AssetType,
        mainCategoryId: mainCategory.id,
        mainCategorySlug: mainCategory.slug,
      }))
    ),
    subCategories: tree.mainCategories.flatMap((mainCategory: any) =>
      mainCategory.categories?.flatMap((category: any) =>
        category.subcategories.map((subCategory: any) => ({
          id: subCategory.id,
          name: subCategory.name,
          slug: subCategory.slug,
          image: subCategory.image,
          title: subCategory.title || subCategory.name,
          description: subCategory.description,
          keywords: parseKeywords(subCategory.keywords),
          mainCategoryId: mainCategory.id,
          categoryId: category.id,
          mainCategorySlug: mainCategory.slug,
          categorySlug: category.slug,
        }))
      )
    ),
    contents: tree.mainCategories.flatMap((mainCategory: any) =>
      mainCategory.categories?.flatMap((category: any) =>
        category.subcategories.flatMap((subCategory: any) =>
          subCategory.contents.map((content: any) => ({
            id: content.id,
            slug: content.slug,
            name: content.name,
            searchTag: 'search_tag' in content ? (content.search_tag as string) : undefined,
            searchQuery: 'search_query' in content ? (content.search_query as string) : undefined,
            searchBroadness: 'search_broadness' in content ? (content.search_broadness as number) : undefined,
            minSearchBroadness:
              'min_search_broadness' in content ? (content.min_search_broadness as number) : undefined,
            searchCategory: 'search_category' in content ? (content.search_category as string) : undefined,
            image: content.image,
            title: content.title || content.name,
            description: content.description,
            keywords: parseKeywords(content.keywords),
            mainCategoryId: mainCategory.id,
            categoryId: category.id,
            subCategoryId: subCategory.id,
            mainCategorySlug: mainCategory.slug,
            categorySlug: category.slug,
            subCategorySlug: subCategory.slug,
          }))
        )
      )
    ),
    filters: [],
  }
}
